import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, Buildings, User, Package, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const SCHOOL_TYPES = [
  ["preschool", "Préscolaire / Maternelle"],
  ["primary", "École primaire"],
  ["college", "Collège"],
  ["lycee", "Lycée"],
  ["complex", "Complexe scolaire"],
  ["institute", "Institut / Grande école"],
  ["university", "Université"],
  ["other", "Autre"],
];

const MODULES = [
  "Élèves/Étudiants","Inscriptions","Gestion académique","Présences","Notes & bulletins",
  "Emplois du temps","Devoirs & documents","Portail parents","Portail élèves","Frais de scolarité",
  "Paiements / Mobile Money","Comptabilité","RH & paie","SMS/WhatsApp","Bibliothèque",
  "Transport","Cantine","Multi-campus","IA / Analytics",
];

const initial = {
  school_name: "", school_type: "", country: "", city: "", address: "",
  student_count: "", teacher_count: "", campus_count: 1,
  contact_first_name: "", contact_last_name: "", contact_role: "",
  contact_phone: "", contact_whatsapp: "", contact_email: "",
  modules_wanted: [], message: "", request_type: "access",
};

export default function Souscription() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleModule = (m) => set("modules_wanted", form.modules_wanted.includes(m)
    ? form.modules_wanted.filter(x => x !== m) : [...form.modules_wanted, m]);

  const canNext = () => {
    if (step === 1) return form.school_name && form.school_type && form.country;
    if (step === 2) return true;
    return form.contact_first_name && form.contact_last_name && form.contact_email;
  };

  const submit = async () => {
    setSending(true);
    try {
      const payload = { ...form,
        student_count: form.student_count ? parseInt(form.student_count) : null,
        teacher_count: form.teacher_count ? parseInt(form.teacher_count) : null,
        campus_count: parseInt(form.campus_count) || 1,
      };
      await api.post("/public/subscription-requests", payload);
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-primary/5 to-accent/5">
      <Card className="max-w-lg p-10 text-center">
        <CheckCircle size={64} weight="duotone" className="mx-auto text-emerald-500 mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Votre demande a bien été reçue !</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Merci de votre intérêt pour Kosyam. Notre équipe examinera votre demande et vous contactera prochainement.
        </p>
        <div className="text-xs text-muted-foreground border-t border-border pt-4 space-y-1">
          <div>📧 contact@nathortechnologies.com</div>
          <div>📱 WhatsApp : sur demande via email</div>
        </div>
        <Button onClick={() => nav("/")} className="mt-6" data-testid="btn-back-home">Retour à l'accueil</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-back">← Kosyam</Link>
          <div className="text-xs text-muted-foreground">Étape {step}/3</div>
        </div>
        <div className="mb-6 h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${step * 33.33}%` }} />
        </div>
        <Card className="p-6 sm:p-8">
          {step === 1 && (
            <>
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-1">
                <Buildings size={24} weight="duotone" className="text-primary" /> Votre établissement
              </h2>
              <p className="text-sm text-muted-foreground mb-6">Parlez-nous de votre établissement.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label>Nom de l'établissement*</Label><Input value={form.school_name} onChange={e=>set("school_name", e.target.value)} data-testid="in-school-name" /></div>
                <div><Label>Type*</Label>
                  <Select value={form.school_type} onValueChange={v=>set("school_type", v)}>
                    <SelectTrigger data-testid="in-school-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{SCHOOL_TYPES.map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Pays*</Label><Input value={form.country} onChange={e=>set("country", e.target.value)} placeholder="BF, CI, SN, GH…" data-testid="in-country" /></div>
                <div><Label>Ville</Label><Input value={form.city} onChange={e=>set("city", e.target.value)} data-testid="in-city" /></div>
                <div><Label>Nombre de campus</Label><Input type="number" min="1" value={form.campus_count} onChange={e=>set("campus_count", e.target.value)} data-testid="in-campus" /></div>
                <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={e=>set("address", e.target.value)} data-testid="in-address" /></div>
                <div><Label>Nombre d'élèves/étudiants (approx.)</Label><Input type="number" value={form.student_count} onChange={e=>set("student_count", e.target.value)} data-testid="in-students" /></div>
                <div><Label>Nombre d'enseignants (approx.)</Label><Input type="number" value={form.teacher_count} onChange={e=>set("teacher_count", e.target.value)} data-testid="in-teachers" /></div>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-1">
                <Package size={24} weight="duotone" className="text-primary" /> Vos besoins
              </h2>
              <p className="text-sm text-muted-foreground mb-6">Cochez les modules qui vous intéressent.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {MODULES.map(m => (
                  <label key={m} className={`text-xs p-2 rounded-lg border cursor-pointer transition-colors ${form.modules_wanted.includes(m) ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"}`}>
                    <input type="checkbox" checked={form.modules_wanted.includes(m)} onChange={()=>toggleModule(m)} className="mr-1.5" data-testid={`mod-${m}`} />
                    {m}
                  </label>
                ))}
              </div>
              <Label>Parlez-nous brièvement de votre établissement ou vos besoins</Label>
              <Textarea rows={4} value={form.message} onChange={e=>set("message", e.target.value)} data-testid="in-message" />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={()=>set("request_type", "access")} className={`p-3 rounded-lg border text-sm text-left ${form.request_type === "access" ? "border-primary bg-primary/10" : "border-border"}`} data-testid="opt-access">
                  <div className="font-medium">Commencer à utiliser</div>
                  <div className="text-xs text-muted-foreground">Créer un espace pour mon établissement</div>
                </button>
                <button onClick={()=>set("request_type", "demo")} className={`p-3 rounded-lg border text-sm text-left ${form.request_type === "demo" ? "border-primary bg-primary/10" : "border-border"}`} data-testid="opt-demo">
                  <div className="font-medium">Demander une démonstration</div>
                  <div className="text-xs text-muted-foreground">Être contacté par l'équipe d'abord</div>
                </button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-1">
                <User size={24} weight="duotone" className="text-primary" /> Vos coordonnées
              </h2>
              <p className="text-sm text-muted-foreground mb-6">Comment vous joindre ?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Prénom*</Label><Input value={form.contact_first_name} onChange={e=>set("contact_first_name", e.target.value)} data-testid="in-fn" /></div>
                <div><Label>Nom*</Label><Input value={form.contact_last_name} onChange={e=>set("contact_last_name", e.target.value)} data-testid="in-ln" /></div>
                <div><Label>Fonction</Label><Input value={form.contact_role} onChange={e=>set("contact_role", e.target.value)} placeholder="Directeur, DAF…" data-testid="in-role" /></div>
                <div><Label>Téléphone</Label><Input value={form.contact_phone} onChange={e=>set("contact_phone", e.target.value)} data-testid="in-phone" /></div>
                <div><Label>WhatsApp</Label><Input value={form.contact_whatsapp} onChange={e=>set("contact_whatsapp", e.target.value)} data-testid="in-wa" /></div>
                <div><Label>Email professionnel*</Label><Input type="email" value={form.contact_email} onChange={e=>set("contact_email", e.target.value)} data-testid="in-email" /></div>
              </div>
            </>
          )}
          <div className="mt-8 flex justify-between">
            <Button variant="outline" onClick={()=>setStep(s=>Math.max(1,s-1))} disabled={step===1} data-testid="btn-prev"><ArrowLeft size={14} className="mr-1" /> Précédent</Button>
            {step < 3 ? (
              <Button onClick={()=>setStep(s=>s+1)} disabled={!canNext()} data-testid="btn-next">Suivant <ArrowRight size={14} className="ml-1" /></Button>
            ) : (
              <Button onClick={submit} disabled={sending || !canNext()} className="bg-primary text-primary-foreground" data-testid="btn-submit">
                {sending ? "Envoi…" : form.request_type === "demo" ? "Demander ma démonstration" : "Envoyer ma demande d'accès"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
